import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface NavLink {
  label: string;
  to: string;
}

const navLinks: NavLink[] = [
  { label: "首页", to: "/" },
  { label: "连载中", to: "/list?status=连载中" },
  { label: "已完结", to: "/list?status=已完结" },
  { label: "常见问题", to: "/faq" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    const base = to.split("?")[0];
    return location.pathname.startsWith(base);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/list?search=${encodeURIComponent(kw)}` : "/list");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? "bg-background-50/95 shadow-sm border-b border-background-200" : "bg-background-50 border-b border-background-200/70"
      }`}
    >
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 md:px-6 h-16">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-book-open-fill text-lg"></i>
          </span>
          <span className="font-display text-xl text-primary-600">无尽漫画网</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className={`rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors ${
                isActive(link.to)
                  ? "bg-primary-100 text-primary-700 font-medium"
                  : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="hidden items-center md:flex">
          <div className="flex items-center rounded-full border border-background-300 bg-background-50 focus-within:border-primary-400">
            <i className="ri-search-line pl-3 text-sm text-secondary-500"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画 / 分类"
              className="w-40 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-secondary-400"
            />
            <button
              type="submit"
              className="mr-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600"
              aria-label="搜索"
            >
              <i className="ri-corner-down-left-line text-sm"></i>
            </button>
          </div>
        </form>

        <button
          onClick={() => setOpen(!open)}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
          aria-label="展开菜单"
        >
          <i className={open ? "ri-close-line text-lg" : "ri-menu-line text-lg"}></i>
        </button>
      </div>

      {open && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 lg:hidden">
          <form onSubmit={handleSearch} className="mb-3 flex items-center rounded-full border border-background-300">
            <i className="ri-search-line pl-3 text-sm text-secondary-500"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画 / 分类"
              className="w-full bg-transparent px-2 py-2 text-sm outline-none"
            />
            <button type="submit" className="mr-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-background-50" aria-label="搜索">
              <i className="ri-corner-down-left-line text-sm"></i>
            </button>
          </form>
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className={`rounded-md px-3 py-2 text-sm ${
                  isActive(link.to) ? "bg-primary-100 text-primary-700 font-medium" : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}